import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  Grid, IconButton, Collapse, Snackbar,
} from '@mui/material';
import {
  Add as AddIcon, KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon, Payment as PayIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Instalment {
  id: number;
  instalment_number: number;
  due_date: string;
  amount: number;
  paid_amount: number;
  paid_date: string | null;
  payment_method: string | null;
  status: string;
}

interface Plan {
  id: number;
  plan_name: string;
  client_name: string;
  client_email: string;
  invoice_number: string;
  total_amount: number;
  down_payment: number;
  number_of_instalments: number;
  frequency: string;
  start_date: string;
  status: string;
  amount_collected: number;
  paid_instalments: number;
  total_instalments: number;
  instalments?: Instalment[];
}

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
  if (s === 'Active') return 'info';
  if (s === 'Completed') return 'success';
  if (s === 'Defaulted') return 'error';
  if (s === 'Cancelled') return 'error';
  return 'default';
};

const instalmentColor = (s: string): 'success' | 'warning' | 'error' | 'default' => {
  if (s === 'Paid') return 'success';
  if (s === 'Partially Paid') return 'warning';
  if (s === 'Overdue') return 'error';
  return 'default';
};

const FREQUENCIES = ['weekly', 'fortnightly', 'monthly', 'quarterly'];

const PaymentPlansPage = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [instalmentsByPlan, setInstalmentsByPlan] = useState<Record<number, Instalment[]>>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [payOpen, setPayOpen] = useState<{ planId: number; instalment: Instalment } | null>(null);
  const [snack, setSnack] = useState('');

  // Create form state
  const [form, setForm] = useState({
    invoice_id: '', client_id: '', plan_name: '', total_amount: '',
    down_payment: '0', number_of_instalments: '3', frequency: 'monthly', start_date: '',
    notes: '',
  });

  // Pay form state
  const [payForm, setPayForm] = useState({ paid_amount: '', payment_method: 'cash', transaction_ref: '' });
  const [deletePlan, setDeletePlan] = useState<Plan | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadPlans = () => {
    setLoading(true);
    api.get<{ data: Plan[] }>(API_ENDPOINTS.PAYMENT_PLANS.LIST)
      .then(res => setPlans(res.data ?? []))
      .catch(() => setError('Failed to load payment plans'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadPlans(); }, []);

  const handleExpand = async (planId: number) => {
    if (expandedId === planId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(planId);
    if (!instalmentsByPlan[planId]) {
      const res = await api.get<{ data: Instalment[] }>(API_ENDPOINTS.PAYMENT_PLANS.INSTALMENTS(planId));
      setInstalmentsByPlan(prev => ({ ...prev, [planId]: res.data ?? [] }));
    }
  };

  const handleCreate = async () => {
    try {
      await api.post(API_ENDPOINTS.PAYMENT_PLANS.CREATE, {
        invoice_id: Number(form.invoice_id),
        client_id: Number(form.client_id),
        plan_name: form.plan_name,
        total_amount: Number(form.total_amount),
        down_payment: Number(form.down_payment),
        number_of_instalments: Number(form.number_of_instalments),
        frequency: form.frequency,
        start_date: form.start_date,
        notes: form.notes || undefined,
      });
      setCreateOpen(false);
      setForm({ invoice_id: '', client_id: '', plan_name: '', total_amount: '', down_payment: '0', number_of_instalments: '3', frequency: 'monthly', start_date: '', notes: '' });
      setSnack('Payment plan created');
      loadPlans();
    } catch {
      setError('Failed to create payment plan');
    }
  };

  const handlePay = async () => {
    if (!payOpen) return;
    try {
      await api.patch(
        API_ENDPOINTS.PAYMENT_PLANS.PAY_INSTALMENT(payOpen.planId, payOpen.instalment.id),
        { paid_amount: Number(payForm.paid_amount), payment_method: payForm.payment_method, transaction_ref: payForm.transaction_ref || undefined }
      );
      setPayOpen(null);
      setPayForm({ paid_amount: '', payment_method: 'cash', transaction_ref: '' });
      setSnack('Payment recorded');
      // Refresh instalments for this plan
      const res = await api.get<{ data: Instalment[] }>(API_ENDPOINTS.PAYMENT_PLANS.INSTALMENTS(payOpen.planId));
      setInstalmentsByPlan(prev => ({ ...prev, [payOpen.planId]: res.data ?? [] }));
      loadPlans();
    } catch {
      setError('Failed to record payment');
    }
  };

  const handleDeletePlan = async () => {
    if (!deletePlan) return;
    setDeleting(true);
    try {
      await api.delete(API_ENDPOINTS.PAYMENT_PLANS.GET(deletePlan.id));
      setDeletePlan(null);
      setSnack('Payment plan cancelled');
      loadPlans();
    } catch { setError('Failed to cancel payment plan'); } finally { setDeleting(false); }
  };

  if (loading) return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>Payment Plans</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          New Plan
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Plan Name</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>Invoice</TableCell>
              <TableCell>Frequency</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Collected</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {plans.map(plan => (
              <>
                <TableRow key={plan.id} hover>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleExpand(plan.id)}>
                      {expandedId === plan.id ? <CollapseIcon /> : <ExpandIcon />}
                    </IconButton>
                  </TableCell>
                  <TableCell><strong>{plan.plan_name}</strong></TableCell>
                  <TableCell>
                    <Typography variant="body2">{plan.client_name}</Typography>
                    <Typography variant="caption" color="textSecondary">{plan.client_email}</Typography>
                  </TableCell>
                  <TableCell>{plan.invoice_number}</TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>{plan.frequency}</TableCell>
                  <TableCell align="right">${plan.total_amount.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography color={plan.amount_collected >= plan.total_amount ? 'success.main' : 'text.primary'}>
                      ${plan.amount_collected.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {plan.paid_instalments}/{plan.total_instalments} instalments
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={plan.status} size="small" color={statusColor(plan.status)} />
                  </TableCell>
                  <TableCell>
                    {plan.status !== 'Completed' && (
                      <Tooltip title="Cancel Plan">
                        <IconButton size="small" color="error" onClick={() => setDeletePlan(plan)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>

                {/* Instalment detail rows */}
                <TableRow key={`${plan.id}-detail`}>
                  <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                    <Collapse in={expandedId === plan.id} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Instalments</Typography>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>#</TableCell>
                              <TableCell>Due Date</TableCell>
                              <TableCell align="right">Amount</TableCell>
                              <TableCell align="right">Paid</TableCell>
                              <TableCell>Paid Date</TableCell>
                              <TableCell>Method</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell />
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(instalmentsByPlan[plan.id] ?? []).map(inst => (
                              <TableRow key={inst.id}>
                                <TableCell>{inst.instalment_number}</TableCell>
                                <TableCell>{new Date(inst.due_date).toLocaleDateString()}</TableCell>
                                <TableCell align="right">${inst.amount.toFixed(2)}</TableCell>
                                <TableCell align="right">${inst.paid_amount.toFixed(2)}</TableCell>
                                <TableCell>{inst.paid_date ? new Date(inst.paid_date).toLocaleDateString() : '-'}</TableCell>
                                <TableCell>{inst.payment_method ?? '-'}</TableCell>
                                <TableCell>
                                  <Chip label={inst.status} size="small" color={instalmentColor(inst.status)} />
                                </TableCell>
                                <TableCell>
                                  {inst.status !== 'Paid' && (
                                    <Button
                                      size="small" startIcon={<PayIcon />}
                                      onClick={() => {
                                        setPayForm({ paid_amount: String(inst.amount - inst.paid_amount), payment_method: 'cash', transaction_ref: '' });
                                        setPayOpen({ planId: plan.id, instalment: inst });
                                      }}
                                    >
                                      Pay
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
            {plans.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">No payment plans found</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Payment Plan</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={6}>
              <TextField fullWidth label="Invoice ID" type="number" size="small"
                value={form.invoice_id} onChange={e => setForm(p => ({ ...p, invoice_id: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Client ID" type="number" size="small"
                value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Plan Name" size="small"
                value={form.plan_name} onChange={e => setForm(p => ({ ...p, plan_name: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Total Amount ($)" type="number" size="small"
                value={form.total_amount} onChange={e => setForm(p => ({ ...p, total_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Down Payment ($)" type="number" size="small"
                value={form.down_payment} onChange={e => setForm(p => ({ ...p, down_payment: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Number of Instalments" type="number" size="small"
                value={form.number_of_instalments} onChange={e => setForm(p => ({ ...p, number_of_instalments: e.target.value }))} />
            </Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Frequency" size="small"
                value={form.frequency} onChange={e => setForm(p => ({ ...p, frequency: e.target.value }))}>
                {FREQUENCIES.map(f => <MenuItem key={f} value={f} sx={{ textTransform: 'capitalize' }}>{f}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Date" type="date" size="small"
                InputLabelProps={{ shrink: true }}
                value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" size="small" multiline rows={2}
                value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={!form.invoice_id || !form.client_id || !form.plan_name || !form.total_amount || !form.start_date}>
            Create Plan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!payOpen} onClose={() => setPayOpen(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Record Payment — Instalment #{payOpen?.instalment.instalment_number}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField fullWidth label="Amount Paid ($)" type="number" size="small"
                value={payForm.paid_amount} onChange={e => setPayForm(p => ({ ...p, paid_amount: e.target.value }))} />
            </Grid>
            <Grid item xs={12}>
              <TextField select fullWidth label="Payment Method" size="small"
                value={payForm.payment_method} onChange={e => setPayForm(p => ({ ...p, payment_method: e.target.value }))}>
                {['cash', 'card', 'bank_transfer', 'eftpos', 'other'].map(m => (
                  <MenuItem key={m} value={m} sx={{ textTransform: 'capitalize' }}>{m.replace('_', ' ')}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Transaction Reference" size="small"
                value={payForm.transaction_ref} onChange={e => setPayForm(p => ({ ...p, transaction_ref: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayOpen(null)}>Cancel</Button>
          <Button variant="contained" onClick={handlePay} disabled={!payForm.paid_amount}>
            Record Payment
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deletePlan} onClose={() => setDeletePlan(null)}>
        <DialogTitle>Cancel Payment Plan</DialogTitle>
        <DialogContent>
          <Typography>Cancel plan "<strong>{deletePlan?.plan_name}</strong>" for <strong>{deletePlan?.client_name}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlan(null)}>Keep</Button>
          <Button variant="contained" color="error" onClick={handleDeletePlan} disabled={deleting}>{deleting ? 'Cancelling...' : 'Cancel Plan'}</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={3000} onClose={() => setSnack('')} message={snack} />
    </Box>
  );
};

export default PaymentPlansPage;
